/**
 * Start a conversation: pick the work item (fixed when opened from one),
 * pick a teammate (an agent), write the first prompt. Starting is also what
 * puts the teammate on the item, so this dialog is the whole of assigning
 * one. The project supplies the environment and vault (the server enforces
 * that whatever this sends). Every conversation gets a computer of its own.
 */
import { useState, type FormEvent } from "react";
import { useProject } from "../store";
import type { Agent } from "../types";
import { agentFits, defaultTeammate, type WorkItem } from "../lib/workbench";
import { buildPrompt } from "../lib/start";
import { describeError } from "../lib/errors";
import { href, navigate } from "../router";
import { AttachmentStrip, useAttachments } from "./Attachments";

export function StartDialog({ itemId, initialAgentId, onClose }: { itemId?: string | null; initialAgentId?: string | null; onClose: () => void }) {
  const { project, items, agents, environments, vaults, startConversation } = useProject();
  const open = items.filter((w) => w.status === "open");
  const fixed = itemId ? items.find((w) => w.id === itemId) ?? null : null;
  const [pickedItem, setPickedItem] = useState<string>(fixed?.id ?? open[0]?.id ?? items[0]?.id ?? "");
  const item: WorkItem | null = fixed ?? items.find((w) => w.id === pickedItem) ?? null;
  const all = [...agents.values()].sort((a, b) => a.name.localeCompare(b.name));
  const onItem = item ? all.filter((a) => item.agentIds.includes(a.id)) : [];
  const others = item ? all.filter((a) => !item.agentIds.includes(a.id)) : all;
  // Who this is about: the one the caller named, or someone already on the
  // item, or the project's default — then anybody.
  const [agentId, setAgentId] = useState<string>(initialAgentId ?? onItem[0]?.id ?? defaultTeammate(project, agents)?.id ?? all[0]?.id ?? "");
  const [prompt, setPrompt] = useState("");
  const [includeNotes, setIncludeNotes] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const attachments = useAttachments(setError);

  const agent = agents.get(agentId) ?? null;
  // Images ride on the first prompt; with nothing said there is no turn to put them on.
  const orphanImages = !!attachments.payload && !(item && buildPrompt(item, prompt, includeNotes));
  const fit = agent ? agentFits(agent, project) : { ok: false as const, reason: "no agent" };
  const envName = project.environmentId ? environments.get(project.environmentId)?.name ?? "?" : agent?.environment_id ? `${environments.get(agent.environment_id)?.name ?? "?"} (agent's own)` : "none";
  const vaultName = project.vaultId ? vaults.get(project.vaultId)?.name ?? "?" : "none";

  async function submit(e: FormEvent) {
    e.preventDefault();
    if (!agent || !item || busy || !fit.ok || orphanImages) return;
    setBusy(true);
    setError(null);
    try {
      const conversation = await startConversation({ item, agent, prompt, includeNotes, images: attachments.payload });
      navigate(href.conversation(project.id, conversation.id));
      onClose();
    } catch (err) {
      setError(describeError(err));
    } finally {
      setBusy(false);
    }
  }

  const option = (a: Agent) => {
    const f = agentFits(a, project);
    return (
      <option key={a.id} value={a.id} disabled={!f.ok}>
        {a.name} ({a.runtime}){f.ok ? "" : ` — ${f.reason}`}
      </option>
    );
  };

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <form className={`modal${attachments.dragging ? " dropping" : ""}`} onClick={(e) => e.stopPropagation()} onSubmit={submit} {...attachments.dropzone}>
        <h2 className="h2">Start a conversation</h2>
        <p className="muted small">
          The conversation is bound to a work item and runs with {project.name}'s environment and vault, on {project.ownerEmail}'s account.
        </p>

        {fixed ? (
          <div className="field">
            <span className="field-label">Work item</span>
            <span className="strong">{fixed.title}</span>
          </div>
        ) : (
          <label>
            Work item
            <select value={pickedItem} onChange={(e) => setPickedItem(e.target.value)} required>
              {items.length === 0 && <option value="">No work items yet</option>}
              {open.map((w) => (
                <option key={w.id} value={w.id}>
                  {w.title}
                </option>
              ))}
              {items.filter((w) => w.status !== "open").length > 0 && (
                <optgroup label="Done">
                  {items
                    .filter((w) => w.status !== "open")
                    .map((w) => (
                      <option key={w.id} value={w.id}>
                        {w.title}
                      </option>
                    ))}
                </optgroup>
              )}
            </select>
          </label>
        )}

        <label>
          Teammate <span className="hint">Whoever you pick joins the work item when the conversation starts.</span>
          <select value={agentId} onChange={(e) => setAgentId(e.target.value)} required>
            {all.length === 0 && <option value="">No agents on this Managoat</option>}
            {onItem.length > 0 && <optgroup label="On this work item">{onItem.map(option)}</optgroup>}
            {others.length > 0 && <optgroup label={onItem.length ? "Rest of the team" : "Team"}>{others.map(option)}</optgroup>}
          </select>
        </label>
        {agent && (
          <div className="muted small">
            {agent.runtime} · {agent.model} · env {envName} · vault {vaultName}
          </div>
        )}
        {agent && !fit.ok && <div className="error">{agent.name} {fit.reason}.</div>}

        <label>
          First prompt <span className="hint">Attach, paste or drop a screenshot to send it with the words.</span>
          <textarea
            rows={5}
            value={prompt}
            onChange={(e) => setPrompt(e.target.value)}
            onPaste={attachments.paste}
            placeholder="What should they do? Leave empty to just bring the computer up."
            autoFocus
          />
        </label>
        <AttachmentStrip items={attachments.items} onRemove={attachments.remove} add={attachments.add} />
        {orphanImages && <div className="error">Write the prompt the images go with — on their own there is no turn to attach them to.</div>}
        {item?.notes.trim() && (
          <label className="check">
            <input type="checkbox" checked={includeNotes} onChange={(e) => setIncludeNotes(e.target.checked)} /> Prepend the work item's notes
          </label>
        )}

        {error && <div className="error">{error}</div>}
        <div className="row end">
          <button type="button" className="secondary" onClick={onClose} disabled={busy}>
            Cancel
          </button>
          <button type="submit" disabled={busy || !agent || !item || !fit.ok || orphanImages}>
            {busy ? "Starting…" : "Start"}
          </button>
        </div>
      </form>
    </div>
  );
}
